/**
 * New-funnel metric graphs: reads directly from metric_graph (days + treatment values).
 * No supplement_metrics, benefits, or ranking — order follows goalExpectedOutcomes.
 */

(function () {
  "use strict";

  var goalExpectedOutcomes = {
    fitness: [
      { metric: "VO2 Max", desc: "Aerobic fitness capacity", value: "+10%", direction: "up" },
      { metric: "1RM", desc: "Maximum strength output", value: "+14%", direction: "up" },
      { metric: "RHR", desc: "Cardiovascular efficiency marker", value: "\u22124 BPM", direction: "down" }
    ],
    strength: [
      { metric: "1RM", desc: "Peak strength capacity", value: "+14%", direction: "up" },
      { metric: "Power", desc: "Explosive force production", value: "+9%", direction: "up" },
      { metric: "HRV", desc: "Recovery readiness signal", value: "+18%", direction: "up" }
    ],
    endurance: [
      { metric: "VO2 Max", desc: "Endurance engine size", value: "+10%", direction: "up" },
      { metric: "Training Intensity", desc: "Sustainable work capacity", value: "+14%", direction: "up" },
      { metric: "RHR", desc: "Aerobic adaptation marker", value: "\u22124 BPM", direction: "down" }
    ],
    fatloss: [
      { metric: "BMR", desc: "Resting calorie burn", value: "+8%", direction: "up" },
      { metric: "RHR", desc: "Metabolic health signal", value: "\u22124 BPM", direction: "down" },
      { metric: "Sleep Quality", desc: "Hormonal regulation support", value: "+15%", direction: "up" }
    ],
    recovery: [
      { metric: "HRV", desc: "Recovery status indicator", value: "+18%", direction: "up" },
      { metric: "RHR", desc: "System stress marker", value: "\u22124 BPM", direction: "down" },
      { metric: "Sleep Quality", desc: "Repair process quality", value: "+15%", direction: "up" }
    ],
    sleep: [
      { metric: "Deep & REM", desc: "Restorative sleep stages", value: "+20%", direction: "up" },
      { metric: "Sleep Latency", desc: "Sleep onset speed", value: "\u221235%", direction: "down" },
      { metric: "Sleep Disruptions", desc: "Nighttime stability marker", value: "\u221240%", direction: "down" }
    ],
    longevity: [
      { metric: "VO2 Max", desc: "Longevity fitness predictor", value: "+10%", direction: "up" },
      { metric: "RHR", desc: "Heart health marker", value: "\u22124 BPM", direction: "down" },
      { metric: "Sleep Quality", desc: "Healthy aging foundation", value: "+15%", direction: "up" }
    ],
    energy: [
      { metric: "HRV", desc: "Nervous system balance", value: "+18%", direction: "up" },
      { metric: "Sleep Quality", desc: "Cognitive recovery quality", value: "+15%", direction: "up" },
      { metric: "VO2 Max", desc: "Energy production capacity", value: "+10%", direction: "up" }
    ]
  };

  if (typeof window !== "undefined") {
    window.goalExpectedOutcomes = goalExpectedOutcomes;
  }

  /**
   * Parse treatment string to a numeric value.
   * Supports: "+10%", "-4%", "-4 BPM", "-0 BPM", etc.
   */
  function parseTreatmentValue(s) {
    if (typeof s !== "string") return 0;
    const t = s.trim();
    const pctMatch = t.match(/^([+-]?\d+(?:\.\d+)?)\s*%$/);
    if (pctMatch) return parseFloat(pctMatch[1]) || 0;
    const bpmMatch = t.match(/^([+-]?\d+(?:\.\d+)?)\s*BPM$/i);
    if (bpmMatch) return parseFloat(bpmMatch[1]) || 0;
    const numMatch = t.replace(/^\+/, "").match(/^-?\d+(?:\.\d+)?/);
    return numMatch ? parseFloat(numMatch[0]) : 0;
  }

  /**
   * Convert one metric's data (keyed by day) to array of [day, numericValue].
   * metric_graph keys are days (e.g. "30", "60", "90"); one value per day.
   */
  function metricValueToPoints(m) {
    if (!m || typeof m !== "object") return [];
    const points = [];
    for (const [dayStr, data] of Object.entries(m)) {
      const day = parseInt(dayStr, 10);
      if (Number.isNaN(day) || !data || typeof data !== "object") continue;
      if ("treatment" in data) {
        points.push([day, parseTreatmentValue(data.treatment)]);
      }
    }
    return points.sort((a, b) => a[0] - b[0]);
  }

  /**
   * Build { [metricName]: [[day, value], ...] } from metric_graph.
   * Single value per day, no aggregation.
   */
  function collectMetricPoints(metricGraph) {
    if (!metricGraph || typeof metricGraph !== "object") return {};
    const result = {};
    for (const [metricName, dayData] of Object.entries(metricGraph)) {
      const points = metricValueToPoints(dayData);
      if (points.length) result[metricName] = points;
    }
    return result;
  }

  function interpolateSegment(dayStart, dayEnd, valStart, valEnd, random) {
    const n = dayEnd - dayStart - 1;
    if (n <= 0) return [];
    const delta = valEnd - valStart;
    const sign = delta >= 0 ? 1 : -1;
    const absDelta = Math.abs(delta);
    const weights = [];
    for (let i = 0; i < n; i++) weights.push(random());
    const total = weights.reduce((a, b) => a + b, 0);
    return weights.map(function (w) {
      return sign * (w / total) * absDelta;
    });
  }

  /**
   * Build daily series from ordered points [day, value]. Days are already in days (no weeks).
   */
  function buildDailySeries(orderedPoints, maxDays, seed) {
    const numDays = maxDays;
    const values = new Array(numDays + 1);
    values[0] = 0;

    let points = [[0, 0]].concat(orderedPoints);
    if (orderedPoints.length && orderedPoints[orderedPoints.length - 1][0] < maxDays) {
      const lastPoint = orderedPoints[orderedPoints.length - 1];
      points = points.concat([[maxDays, lastPoint[1]]]);
    } else if (!orderedPoints.length) {
      points = [[0, 0], [maxDays, 0]];
    }

    const rng = (function (s) {
      return function () {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        return s / 0x7fffffff;
      };
    })(seed);

    for (let i = 0; i < points.length - 1; i++) {
      const d0 = points[i][0];
      const v0 = points[i][1];
      const d1 = points[i + 1][0];
      const v1 = points[i + 1][1];
      values[d0] = v0;
      values[d1] = v1;
      const increments = interpolateSegment(d0, d1, v0, v1, rng);
      let cur = v0;
      for (let k = 0; k < increments.length; k++) {
        cur += increments[k];
        values[d0 + 1 + k] = Math.round(cur * 1e4) / 1e4;
      }
    }

    const lastVal = values[numDays] != null
      ? values[numDays]
      : (orderedPoints.length ? orderedPoints[orderedPoints.length - 1][1] : 0);
    for (let i = 0; i <= numDays; i++) {
      if (values[i] == null) values[i] = lastVal;
    }
    return values;
  }

  /**
   * Get ordered metric names for the current goal (from goalExpectedOutcomes).
   */
  function getOrderedMetricNames() {
    const outcomes = typeof window !== "undefined" ? window.goalExpectedOutcomes : goalExpectedOutcomes;
    if (!outcomes) return [];
    const goal = (typeof window !== "undefined" && window.currentGoal) ? window.currentGoal : "fitness";
    const list = outcomes[goal];
    if (!Array.isArray(list)) return [];
    return list.map(function (o) { return o.metric; });
  }

  /**
   * Build per-metric graph data from metric_graph.
   * Sets window.metricsGraphNewFunnel and dispatches "metricsGraphNewFunnelReady".
   */
  async function getMetricGraphs(seed) {
    const metricGraph = (typeof metric_graph !== "undefined")
      ? metric_graph
      : (typeof window !== "undefined" ? window.metric_graph : null);

    if (!metricGraph || typeof metricGraph !== "object") {
      if (typeof window !== "undefined") {
        window.metricsGraphNewFunnel = {};
        window.dispatchEvent(new CustomEvent("metricsGraphNewFunnelReady", { detail: { graphs: {} } }));
      }
      return { metrics: {} };
    }

    seed = seed ?? 42;
    const metricPoints = collectMetricPoints(metricGraph);
    const graphs = {};
    for (const [metricName, points] of Object.entries(metricPoints)) {
      if (!points.length) continue;
      const maxDays = Math.max.apply(null, points.map(function (p) { return p[0]; }));
      const valuesByDay = buildDailySeries(points, maxDays, seed);
      graphs[metricName] = {
        max_days: maxDays,
        num_days: maxDays,
        known_points: points.map(function (p) {
          return { day: p[0], value: Math.round(p[1] * 1e4) / 1e4 };
        }),
        values_by_day: valuesByDay
      };
    }

    var orderedGraphs = {};
    var order = getOrderedMetricNames();
    for (var i = 0; i < order.length; i++) {
      var name = order[i];
      if (graphs[name]) orderedGraphs[name] = graphs[name];
    }
    for (var metricName in graphs) {
      if (!orderedGraphs[metricName]) orderedGraphs[metricName] = graphs[metricName];
    }

    if (typeof window !== "undefined") {
      window.metricsGraphNewFunnel = orderedGraphs;
      window.dispatchEvent(new CustomEvent("metricsGraphNewFunnelReady", { detail: { graphs: orderedGraphs } }));
    }
    console.log('orderedGraphs', orderedGraphs);
    return { metrics: orderedGraphs };
  }

  function runMetricGraphs() {
    getMetricGraphs().catch(function (err) {
      console.error("getMetricGraphs (new funnel) failed:", err);
    });
  }

  if (typeof window !== "undefined") {
    window.getMetricGraphsNewFunnel = getMetricGraphs;
    window.runMetricGraphsNewFunnel = runMetricGraphs;
  }
})();
